/**
 * Fallback shown when an error reaches the UI but carries no readable message.
 */
export const DEFAULT_ERROR_MESSAGE = "Something went wrong.";

/**
 * Shown when the backend reports it cannot reach MongoDB. Names the database on
 * purpose: the point of SCRUM-1432 is that this failure was indistinguishable
 * from any other, so a generic string here would not fix anything.
 */
export const DATABASE_UNAVAILABLE_MESSAGE =
  "The database is unreachable, so the server cannot answer right now. This is a server problem, not your login.";

/**
 * Shown when a request failed without any response to read — the server could
 * not be reached (network down, backend restarting, request aborted). The
 * errors slice stores this for a `GET_ERRORS` whose payload is `undefined`
 * (`err.response?.data` on such a failure), so it is not silently dropped
 * (SCRUM-2001).
 */
export const NETWORK_ERROR_MESSAGE =
  "The server could not be reached. Check your connection and try again.";

/**
 * Normalize the many payload shapes that client thunks dispatch as `GET_ERRORS`
 * into a single user-facing string. Shapes seen across the client (see
 * `services/*Services/*Action*.js`):
 *
 *   - `{ errors: [{ msg }] }`  express-validator response body
 *   - `{ message }`            Error / custom API error body
 *   - `{ msg }`                some controllers
 *   - `{ error }`              e.g. `{ error: 'Internal Server Error' }` (SCRUM-1263)
 *   - `{ err }`                e.g. `uploadImg`'s `{ err: '...' }` (SCRUM-1263)
 *   - `"..."`                  a plain string payload
 *
 * Anything unrecognized (but present) falls back to {@link DEFAULT_ERROR_MESSAGE}
 * so an error is never swallowed silently. Always returns a non-empty string.
 *
 * @param errors the `state.errors` payload
 */
/**
 * Shown instead of an HTML document a thunk received as its error body — the
 * load balancer's own `503 Service Temporarily Unavailable` page while no
 * backend task is registered (SCRUM-2384). The status is read off the page's
 * `<title>`, because the thunks dispatch the body without the response status.
 */
function serverUnreachableMessage(status?: number | string): string {
  return status
    ? `The CMS server is temporarily unreachable (HTTP ${status}). Try again in a minute.`
    : "The CMS server is temporarily unreachable. Try again in a minute.";
}

export default function getErrorMessage(errors: unknown): string {
  if (typeof errors === "string") {
    if (/<html|<!doctype/i.test(errors)) {
      return serverUnreachableMessage(/<title>\s*(\d{3})\b/i.exec(errors)?.[1]);
    }
    return errors.trim() || DEFAULT_ERROR_MESSAGE;
  }

  if (errors && typeof errors === "object") {
    const e = errors as any;

    // { status:'unavailable', checks:{ mongo:'disconnected' } } — the 503 a backend
    // that cannot reach MongoDB now answers with (server/helpers/healthCheck.ts,
    // SCRUM-1432). Checked first: this body carries no msg/message/error key, so
    // without a branch of its own a dead database reads as the generic fallback —
    // strictly less informative than a typo'd password, which does get a real
    // message. That contrast is the defect.
    if (e.checks && e.checks.mongo === "disconnected") {
      return DATABASE_UNAVAILABLE_MESSAGE;
    }

    // express-validator: { errors: [{ msg }] } — first entry with a real message.
    if (Array.isArray(e.errors)) {
      for (const item of e.errors) {
        const itemMsg = item && (item.msg || item.message);
        if (typeof itemMsg === "string" && itemMsg.trim()) return itemMsg;
      }
    }

    // { error:'DUPLICATE_LISTING', subcategory, existingPath } — the 409 a second
    // published listing for one subcategory gets (server historiespages.js,
    // SCRUM-1670/1976). Checked before the generic `error` key, which would show
    // the bare code (SCRUM-1982). Same wording as the add-page form
    // (PageFormContainer.tsx); existingPath is null from the index backstop.
    if (e.error === "DUPLICATE_LISTING") {
      return typeof e.existingPath === "string" && e.existingPath.trim()
        ? `This subcategory already has a published listing on this site: ${e.existingPath}`
        : "This subcategory already has a published listing on this site.";
    }

    // { error:'DUPLICATE_CODE', existingId } — the 409 of both category create
    // routes (SCRUM-1673) names the node that already carries the code. Same
    // wording the Add Category screen showed itself before the hub took over the
    // toast (SCRUM-2290); checked before the generic `error` key for the same reason.
    if (e.error === "DUPLICATE_CODE") {
      return e.existingId
        ? `Code is already used (node ${e.existingId})`
        : "Code is already used";
    }

    if (typeof e.message === "string" && e.message.trim()) return e.message;
    if (typeof e.msg === "string" && e.msg.trim()) return e.msg;
    if (typeof e.error === "string" && e.error.trim()) return e.error;
    if (typeof e.err === "string" && e.err.trim()) return e.err;
  }

  return DEFAULT_ERROR_MESSAGE;
}

/**
 * Whether an `state.errors` payload represents an error worth surfacing to the
 * user. The errors slice is `{}` initially and after `CLEAR_ERRORS`, and several
 * success paths dispatch `GET_ERRORS` with `{}` or `null` to clear — those must
 * NOT raise a toast. Only a non-empty string or a non-empty object counts.
 *
 * @param errors the `state.errors` payload
 */
export function hasDisplayableError(errors: unknown): boolean {
  if (!errors) return false; // null / undefined / ""
  if (typeof errors === "string") return errors.trim().length > 0;
  if (Array.isArray(errors)) return errors.length > 0;
  if (typeof errors === "object") {
    // `{ errors: null }` is a cleared slice wearing a wrapper: an object, so the
    // old `Object.keys().length > 0` read it as a real error. Nothing in the
    // client dispatches that shape — but seven test fixtures preload it, and any
    // screen that toasts on this predicate would fire "Something went wrong." on
    // first render for it (found while fixing SCRUM-1432, when LoginContainer
    // started using this predicate and the toast fired 3x instead of 1x).
    // An object whose every own value is null/undefined carries nothing to show.
    return Object.values(errors).some((v) => v !== null && v !== undefined);
  }
  return false;
}
